import { Box, CircularProgress } from '@mui/material';
import { getOperativeProfileData } from 'lib/api/business';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import CreditLines from './Components/CreditLines';
import Documents from './Components/Documents';
import Insights from './Components/Insights';
import Summary from './Components/Summary';
import { generateOperativeProfilePDF } from './Utils/generatePDF';

type CreditLineSummary = {
  amount: number;
  status: string;
  lineName: string;
  validUntil: Date | null;
  subProducts: { name: string; amount: number }[];
};

export type ByTypeItem = {
  name: string;
  nameEsp: string;
  quantity: number;
  amount: number;
};

export type ClassifiedByType = {
  'electronic-invoice': ByTypeItem;
  'check-invoice': ByTypeItem;
  'buy-order': ByTypeItem;
  total: ByTypeItem;
};

export interface OperativeProfileData {
  businessName: string;
  inFavorAccounts: Array<{
    name: string;
    total: number;
    quantity?: number;
  }>;
  inFavorTotal: number;
  againstAccounts: Array<{
    name: string;
    total: number;
    arrearTrenches?: {
      name: string;
      amount: number;
      quantity: number;
    }[];
  }>;
  againstTotal: number;
  arrearsAccounts: Array<{
    name: string;
    total: number;
  }>;
  arrearsTotal: number;
  pendingAccountsInFavor: object[];
  pendingAccountsAgainst: object[];
  factoringDocumentsAsClient: object[];
  factoringDocumentsAsStakeholder: object[];
  creditDocuments: object[];
  confirmingDocuments: object[];
  sgrDocuments: object[];
  factoringCreditLine: CreditLineSummary | null;
  confirmingCreditLine: CreditLineSummary | null;
  creditCreditLine: CreditLineSummary | null;
  sgrCreditLine: CreditLineSummary | null;
  factoringCreditLineUsedAmount: number;
  confirmingCreditLineUsedAmount: number;
  creditCreditLineUsedAmount: number;
  sgrCreditLineUsage: {
    totalUsage: number;
    offerSeriousnessUsage: number;
    faithfulComplianceUsage: number;
  };
  insights: {
    factoring: {
      factoringBoughtDocumentsQuantity: number;
      factoringPaidDocumentsQuantity: number;
      factoringActiveDocumentsInArrearsQuantity: number;
      factoringActiveDocumentsOnDateQuantity: number;
      factoringActiveStakeholdersQuantity: number;
      factoringHistoricStakeholdersQuantity: number;
      factoringAveragePaymentDays: number;
      factoringBoughtDocumentAmount: number;
      factoringPaidDocumentAmount: number;
      factoringOnDateDocumentAmount: number;
      factoringInArrearsDocumentAmount: number;
      factoringAverageBoughtDocumentAmount: number;
      factoringAverageCurseDays: number;
      factoringConciliatedByBusinessAmount: number;
      factoringConciliatedByStakeholderAmount: number;
    };
    confirming: {
      confirmingPaidDocumentsQuantity: number;
      confirmingActiveDocumentsInArrearsQuantity: number;
      confirmingActiveDocumentsOnDateQuantity: number;
      confirmingBoughtDocumentsQuantity: number;
      confirmingActiveBusinessesQuantity: number;
      confirmingHistoricBusinessesQuantity: number;
      confirmingAveragePaymentDays: number;
      confirmingBoughtDocumentAmount: number;
      confirmingPaidDocumentAmount: number;
      confirmingOnDateDocumentAmount: number;
      confirmingInArrearsDocumentAmount: number;
      confirmingAverageBoughtDocumentAmount: number;
      confirmingAverageCurseDays: number;
      confirmingConciliatedByBusinessAmount: number;
      confirmingConciliatedByStakeholderAmount: number;
    };
    credit: {
      creditPaidDocumentsQuantity: number;
      creditActiveDocumentsInArrearsQuantity: number;
      creditActiveDocumentsOnDateQuantity: number;
      creditAverageDocumentAmount: number;
      creditPaidDocumentsAmount: number;
      creditActiveDocumentsOnDateAmount: number;
      creditActiveDocumentsInArrearsAmount: number;
    };
  };
  extensions: {
    active: {
      quantity: number;
      amount: number;
    };
    completed: {
      quantity: number;
      amount: number;
    };
  };
  activeMoneyByType: ClassifiedByType;
}

interface OperativeProfileProps {
  businessIdentifier: string | null;
}

const OperativeProfile: React.FC<OperativeProfileProps> = ({
  businessIdentifier,
}) => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<OperativeProfileData | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const dispatch = useDispatch();

  const fetchOperativeProfileData = async () => {
    setLoading(true);
    try {
      if (!businessIdentifier) return;
      const image = await fetch(
        'https://pruff-public.s3.amazonaws.com/company-logos/4.png',
      );
      const arrayBuffer = await image.arrayBuffer();
      const base64String = btoa(
        String.fromCharCode(...new Uint8Array(arrayBuffer)),
      );
      const imageBase64 = `data:image/png;base64,${base64String}`;
      setImageBase64(imageBase64);
      const response = await getOperativeProfileData(
        dispatch,
        businessIdentifier,
      );
      setData(response);
    } catch (err) {
      showAlert(
        dispatch,
        'error',
        'Error al cargar los datos del perfil operativo',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!imageBase64) return;
    generateOperativeProfilePDF(
      data as OperativeProfileData,
      imageBase64,
      user,
    );
  };

  useEffect(() => {
    if (businessIdentifier) {
      void fetchOperativeProfileData();
    }
  }, [businessIdentifier]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Summary data={data} handleExportPDF={() => void handleExportPDF()} />
      <CreditLines
        data={data}
        businessIdentifier={businessIdentifier}
        refetchCreditLines={fetchOperativeProfileData}
      />
      <Insights data={data} />
      <Documents data={data} />
    </Box>
  );
};

export default OperativeProfile;
